"use client";

import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import type { ValidationResult } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { Chip } from "@/components/Chip/Chip";
import {
  Description,
  FieldError,
  getInputStateStyles,
  InputWrapper,
  Label,
  useInputFocus,
} from "@/components/Field/Field";
import { useDebounce } from "@/hooks/useDebounce";
import type { Size } from "@/utils/controlStyles";

interface Item {
  id: string;
  name: string;
}

export interface ChipInputFieldProps {
  /** Field label */
  label?: string;
  /** Field description */
  description?: string;
  /** Error message to display */
  errorMessage?: string | ((validation: ValidationResult) => string);
  /** Size variant */
  size?: Size;
  /** Tooltip for the label */
  tooltip?: string;
  /** Whether the field is required */
  isRequired?: boolean;
  /** Whether the field is disabled */
  isDisabled?: boolean;
  /** Whether the field is invalid */
  isInvalid?: boolean;
  /** Validation result */
  validationResult?: ValidationResult;
  /** Placeholder text for the input */
  placeholder?: string;
  /** Selected items (array of item IDs) */
  selectedItems?: string[];
  /** Callback when selected items change */
  onItemsChange?: (items: string[]) => void;
  /** Static items for autocomplete suggestions */
  staticItems?: Item[];
  /** Async function to fetch suggestions (alternative to staticItems) */
  fetchSuggestions?: (query: string) => Promise<string[]>;
  /** Custom chip variant */
  chipVariant?: "default" | "brand" | "secondary" | "info";
  /** Whether to show errors */
  showErrors?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * ChipInputField
 *
 * A tags input component with autocomplete suggestions.
 * Press Enter or comma to add tags, Backspace when empty to remove last tag.
 * Supports both static items and async fetching for suggestions.
 */
export function ChipInputField({
  label,
  description,
  errorMessage,
  size = "md",
  tooltip,
  isRequired = false,
  isDisabled = false,
  isInvalid = false,
  validationResult,
  placeholder = "Add items...",
  selectedItems = [],
  onItemsChange,
  staticItems,
  fetchSuggestions,
  chipVariant = "default",
  showErrors = true,
  className,
}: ChipInputFieldProps) {
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const debouncedInputValue = useDebounce(inputValue, 300);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { isFocused, handleFocus, handleBlur } = useInputFocus();
  const listboxId = useId();

  // Add a tag
  const addTag = useCallback(
    (value: string) => {
      if (!onItemsChange || !value.trim()) return;

      const trimmedValue = value.trim();

      // Don't add duplicates
      if (selectedItems.includes(trimmedValue)) {
        setInputValue("");
        setShowDropdown(false);
        setSuggestions([]);
        setSelectedIndex(-1);
        return;
      }

      onItemsChange([...selectedItems, trimmedValue]);
      setInputValue("");
      setShowDropdown(false);
      setSuggestions([]);
      setSelectedIndex(-1);
      inputRef.current?.focus();
    },
    [selectedItems, onItemsChange]
  );

  // Remove a tag
  const removeTag = useCallback(
    (itemId: string) => {
      if (!onItemsChange) return;
      onItemsChange(selectedItems.filter((id) => id !== itemId));
    },
    [selectedItems, onItemsChange]
  );

  // Fetch suggestions when input changes (async mode)
  useEffect(() => {
    if (!fetchSuggestions) return;

    let isActive = true;

    const fetchData = async () => {
      const query = debouncedInputValue.trim();
      if (!query) {
        if (isActive) {
          setIsLoading(false);
          setSuggestions([]);
          setShowDropdown(false);
        }
        return;
      }

      setIsLoading(true);
      try {
        const results = await fetchSuggestions(query);
        if (!isActive) return;
        setSuggestions(results);
        setShowDropdown(true);
      } catch (error) {
        if (!isActive) return;
        console.error("Error fetching suggestions:", error);
        setSuggestions([]);
        setShowDropdown(false);
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    fetchData();
    return () => {
      isActive = false;
    };
  }, [debouncedInputValue, fetchSuggestions]);

  // Filter static items when input changes (static mode)
  useEffect(() => {
    if (fetchSuggestions || !staticItems) return;

    if (!debouncedInputValue.trim()) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    const filtered = staticItems
      .filter((item) => item.name.toLowerCase().includes(debouncedInputValue.toLowerCase()))
      .map((item) => item.id);
    setSuggestions(filtered);
    setShowDropdown(filtered.length > 0 || debouncedInputValue.trim().length > 0);
  }, [debouncedInputValue, staticItems, fetchSuggestions]);

  // Compute display suggestions (filter out already selected, add "create new" option)
  const displaySuggestions = useMemo(() => {
    const filtered = suggestions.filter((s) => !selectedItems.includes(s));
    if (inputValue.trim() && !filtered.includes(inputValue.trim()) && !selectedItems.includes(inputValue.trim())) {
      return [...filtered, `Create tag: "${inputValue.trim()}"`];
    }
    return filtered;
  }, [suggestions, inputValue, selectedItems]);

  // Handle input change
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setSelectedIndex(-1);
    if (e.target.value) {
      setShowDropdown(true);
    }
  }, []);

  // Handle suggestion click
  const handleSuggestionClick = useCallback(
    (suggestion: string) => {
      if (isDisabled) return;

      if (suggestion.startsWith("Create tag:")) {
        addTag(inputValue);
      } else {
        addTag(suggestion);
      }
    },
    [addTag, inputValue, isDisabled]
  );

  // Handle key down
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (isDisabled) return;

      switch (e.key) {
        case "Enter":
        case ",":
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < displaySuggestions.length) {
            const suggestion = displaySuggestions[selectedIndex];
            if (suggestion.startsWith("Create tag:")) {
              addTag(inputValue);
            } else {
              addTag(suggestion);
            }
          } else if (inputValue.trim()) {
            addTag(inputValue.trim());
          }
          break;
        case "Backspace":
          if (!inputValue && selectedItems.length > 0) {
            e.preventDefault();
            removeTag(selectedItems[selectedItems.length - 1]);
          }
          break;
        case "ArrowDown":
          e.preventDefault();
          if (showDropdown) {
            setSelectedIndex((prev) => (prev < displaySuggestions.length - 1 ? prev + 1 : prev));
          }
          break;
        case "ArrowUp":
          e.preventDefault();
          if (showDropdown) {
            setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
          }
          break;
        case "Escape":
          setShowDropdown(false);
          setSelectedIndex(-1);
          break;
      }
    },
    [isDisabled, inputValue, selectedItems, displaySuggestions, selectedIndex, showDropdown, addTag, removeTag]
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && e.target instanceof Node && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Get container styles using the same pattern as TextField/Autocomplete
  const getContainerStyles = () => {
    return twMerge(
      "flex items-center gap-1.5 rounded-md overflow-x-auto overflow-y-hidden",
      // Hide scrollbars
      "[&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]",
      getInputStateStyles({
        isInvalid,
        isDisabled,
        isFocused,
      }),
      "bg-background-input",
      size === "sm" && "px-2 py-1 h-[32px]",
      size === "md" && "px-2.5 py-1.5 h-[40px]",
      size === "lg" && "px-3 py-2 h-[44px]",
      size === "xl" && "px-3.5 py-2.5 h-[48px]",
      isDisabled && "opacity-50 cursor-not-allowed bg-background-muted"
    );
  };

  return (
    <div ref={containerRef} className={twMerge("relative flex flex-col gap-1", className)}>
      {label && (
        <Label size={size} tooltip={tooltip} isRequired={isRequired}>
          {label}
        </Label>
      )}

      {/* Main input container */}
      <InputWrapper>
        <div className={getContainerStyles()} onClick={() => !isDisabled && inputRef.current?.focus()}>
          {/* Selected chips */}
          {selectedItems.map((itemId) => (
            <Chip
              key={itemId}
              variant={chipVariant}
              size="xs"
              onRemove={() => removeTag(itemId)}
              isDisabled={isDisabled}
              className="shrink-0 max-h-full"
            >
              {itemId}
            </Chip>
          ))}

          {/* Input field - combobox role on input per ARIA best practices */}
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded={showDropdown}
            aria-haspopup="listbox"
            aria-controls={listboxId}
            aria-autocomplete="list"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={(e) => handleFocus(e as React.FocusEvent<HTMLInputElement>)}
            onBlur={(e) => handleBlur(e as React.FocusEvent<HTMLInputElement>)}
            placeholder={selectedItems.length === 0 ? placeholder : ""}
            disabled={isDisabled}
            className="flex-1 min-w-[120px] h-full !border-0 !outline-none !ring-0 bg-transparent text-text-body text-sm placeholder:text-text-placeholder disabled:cursor-not-allowed focus:!ring-0 focus:!outline-none focus:!border-0 focus-visible:!ring-0 focus-visible:!outline-none focus-visible:!border-0"
            style={{
              boxShadow: "none",
              WebkitAppearance: "none",
              appearance: "none",
              border: "none",
              outline: "none",
            }}
          />
        </div>
      </InputWrapper>

      {/* Suggestions dropdown */}
      {showDropdown && (inputValue || displaySuggestions.length > 0) && (
        <div
          id={listboxId}
          role="listbox"
          className={twMerge(
            "absolute z-50 mt-1 rounded-lg border border-border-default",
            "max-h-60 overflow-auto bg-background-surface shadow-lg",
            "left-0 right-0 top-full"
          )}
        >
          {isLoading ? (
            <div className="px-3 py-2 text-sm text-text-muted">Loading...</div>
          ) : displaySuggestions.length > 0 ? (
            displaySuggestions.map((suggestion, index) => (
              <div
                key={suggestion}
                role="option"
                aria-selected={index === selectedIndex}
                className={twMerge(
                  "cursor-pointer px-3 py-2 text-sm transition-colors",
                  "hover:bg-background-hover",
                  index === selectedIndex && "bg-background-selected",
                  suggestion.startsWith("Create tag:") && "italic text-text-muted"
                )}
                onClick={() => handleSuggestionClick(suggestion)}
              >
                {suggestion}
              </div>
            ))
          ) : (
            inputValue && <div className="px-3 py-2 text-sm text-text-muted">No suggestions found</div>
          )}
        </div>
      )}

      {description && (
        <Description size={size} className="mt-1">
          {description}
        </Description>
      )}

      {showErrors && typeof errorMessage === "string" && (
        <FieldError size={size} className="text-feedback-error-text mt-1">
          {errorMessage}
        </FieldError>
      )}

      {showErrors && typeof errorMessage === "function" && validationResult && (
        <FieldError size={size} className="text-feedback-error-text mt-1">
          {errorMessage(validationResult)}
        </FieldError>
      )}
    </div>
  );
}
